'use client';

import Image from 'next/image';
import {useTranslations} from 'next-intl';
import {type FC, memo} from 'react';
import {StatIcon} from '@/components/atoms/StatIcon';
import {StorageImage} from '@/components/atoms/StorageImage';
import {ReferralLinkInput} from '@/entities/User/ui/UserInfo/ReferralLinkInput';
import {Modal} from '@/utils/antd-imports';

interface AirdropShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Share URL from API (POST airdrop-share). */
  shareUrl: string;
  /** Optional "Last week total reward" block. Figma 2889-249906, 2889-249650. */
  rewardPreview?: {
    label: string;
    amount: string;
    imageUrl?: string;
  };
}

/**
 * @description Share modal for platform_share_airdrop. Figma 2889-249906 (Mobile), 2889-249650 (PC).
 * Title, optional reward preview image, "Join now — use my referral:", link field + social buttons.
 */
export const AirdropShareModal: FC<AirdropShareModalProps> = memo(({isOpen, onClose, shareUrl, rewardPreview}) => {
  const t = useTranslations('experiences.card');

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closeIcon={false}
      centered
      className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-wrap]:!bg-black/60 [&_.ant-modal-content]:max-w-[288px] md:[&_.ant-modal-content]:max-w-[560px] [&_.ant-modal-content]:mx-auto [&_.ant-modal-content]:w-full"
      styles={{
        body: {padding: 0},
      }}
      width={'100%'}
    >
      <div className="relative flex flex-col items-center gap-4 rounded-[24px] bg-[#1E1E2A] px-6 py-8 md:gap-6 md:px-8 md:py-12 font-['Inter']">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex size-10 items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-2 transition-opacity hover:opacity-80 md:right-4 md:top-4"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Close</title>
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#E9ECF0"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex flex-col gap-4 items-center px-9 md:gap-6 md:px-12 w-full">
          <h2 className="text-center font-['inter'] text-[20px] font-bold leading-7 text-[#E9ECF0] md:text-[36px] md:leading-normal">
            {t('airdrop_share_title')}
          </h2>

          {rewardPreview && (
            <div className="relative flex flex-col items-center justify-end w-full rounded-2xl overflow-hidden bg-[#131424] min-h-[164px] md:min-h-[256px] md:rounded-[20px]">
              {rewardPreview.imageUrl ? (
                <>
                  <div className="absolute inset-0">
                    <StorageImage
                      src={rewardPreview.imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      withBlur
                      sources={[
                        {media: '(min-width: 768px)', width: 256, height: 256, resize: 'cover'},
                        {media: '(min-width: 0px)', width: 164, height: 164, resize: 'cover'},
                      ]}
                    />
                  </div>
                  <div
                    className="absolute inset-0 rounded-2xl md:rounded-[20px]"
                    style={{
                      background:
                        'linear-gradient(180.26deg, rgba(30, 31, 47, 0.1) 36.34%, rgba(30, 31, 47, 0.7) 68.06%, #1E1F2F 99.78%)',
                    }}
                  />
                </>
              ) : (
                <div
                  className="absolute inset-0 rounded-2xl md:rounded-[20px]"
                  style={{
                    background: 'linear-gradient(180deg, rgba(30, 31, 47, 0.6) 0%, rgba(26, 27, 42, 0.9) 100%)',
                  }}
                />
              )}
              <div className="relative flex flex-col gap-2 md:gap-4 items-center p-4 md:p-6 w-full">
                <p className="font-['Inter'] text-[14px] md:text-[20px] font-semibold leading-[1.2] text-[#EBECFE] text-center">
                  {rewardPreview.label}
                </p>
                <div className="flex items-center gap-1.5 md:gap-2 h-9 md:h-14 rounded-[36px] md:rounded-[56px] bg-[rgba(26,27,42,0.6)] backdrop-blur-sm pl-2 md:pl-4 pr-3 md:pr-5 py-2 md:py-3">
                  <StatIcon type="crystals" className="size-5 md:size-8" />
                  <span className="font-['Inter'] text-[18px] md:text-[28px] font-semibold leading-[1.16] text-[#E9ECF0]">
                    {rewardPreview.amount}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4 md:gap-6 w-full md:max-w-[406px]">
            <p className="text-center font-['inter'] text-[16px] font-medium leading-5 text-[rgba(233,236,240,0.8)] md:text-[20px] md:leading-[1.2]">
              {t('airdrop_join_referral')}
            </p>
            <ReferralLinkInput className="w-full md:max-w-[336px] mx-auto" initialLink={shareUrl} variant="modal" />
          </div>
        </div>
      </div>
    </Modal>
  );
});

AirdropShareModal.displayName = 'AirdropShareModal';
